import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart3, 
  Loader2,
  Users,
  Clock,
  Calendar,
  Trophy,
  TrendingUp,
  Medal
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StatisticheVolontariato() {
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState(null);
  
  const currentYear = new Date().getFullYear();
  const [anno, setAnno] = useState(currentYear.toString());
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    loadStatistiche();
  }, [anno]);

  const loadStatistiche = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/reports/statistiche-volontariato?anno=${anno}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setStatsData(data);
      } else {
        throw new Error('Errore nel caricamento');
      }
    } catch (error) {
      toast.error('Errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055BB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="statistiche-volontariato-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Statistiche Volontariato
          </h1>
          <p className="text-slate-500 mt-1">
            Classifiche, trend e analisi delle attività di volontariato
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Anno:</span>
          <Select value={anno} onValueChange={setAnno}>
            <SelectTrigger className="w-32" data-testid="select-anno">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-sm opacity-80">Volontari Attivi</p>
                <p className="text-3xl font-bold">{statsData?.totali?.volontari_attivi || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-sm opacity-80">Eventi Totali</p>
                <p className="text-3xl font-bold">{statsData?.totali?.totale_eventi || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-sm opacity-80">Ore Totali</p>
                <p className="text-3xl font-bold">{statsData?.totali?.totale_ore || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-sm opacity-80">Presenze Totali</p>
                <p className="text-3xl font-bold">{statsData?.totali?.totale_presenze || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leaderboards */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Volontari per Attività */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Classifica per Numero Attività
            </CardTitle>
            <CardDescription>Volontari con più partecipazioni nel {anno}</CardDescription>
          </CardHeader>
          <CardContent>
            {statsData?.volontari_piu_attivi?.per_numero_attivita?.length > 0 ? (
              <div className="space-y-3">
                {statsData.volontari_piu_attivi.per_numero_attivita.slice(0, 10).map((vol, idx) => (
                  <div key={vol.volontario_id} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                      idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : 'bg-slate-300'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{vol.nome}</p>
                      <p className="text-xs text-slate-500">{vol.ore_totali} ore totali</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#0055BB]">{vol.num_attivita}</p>
                      <p className="text-xs text-slate-500">attività</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">Nessun dato disponibile</p>
            )}
          </CardContent>
        </Card>

        {/* Top Volontari per Ore */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Medal className="w-5 h-5 text-purple-500" />
              Classifica per Ore di Volontariato
            </CardTitle>
            <CardDescription>Volontari con più ore donate nel {anno}</CardDescription>
          </CardHeader>
          <CardContent>
            {statsData?.volontari_piu_attivi?.per_ore_volontariato?.length > 0 ? (
              <div className="space-y-3">
                {statsData.volontari_piu_attivi.per_ore_volontariato.slice(0, 10).map((vol, idx) => (
                  <div key={vol.volontario_id} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                      idx === 0 ? 'bg-purple-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-purple-700' : 'bg-slate-300'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{vol.nome}</p>
                      <p className="text-xs text-slate-500">{vol.num_attivita} attività</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-purple-600">{vol.ore_totali}</p>
                      <p className="text-xs text-slate-500">ore</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">Nessun dato disponibile</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Attività più frequenti e Distribuzione mensile */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Tipologie Attività */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#0055BB]" />
              Tipologie Attività più Frequenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsData?.attivita_piu_frequenti?.length > 0 ? (
              <div className="space-y-3">
                {statsData.attivita_piu_frequenti.map((tip, idx) => {
                  const maxCount = Math.max(...statsData.attivita_piu_frequenti.map(t => t.count));
                  const percentage = (tip.count / maxCount) * 100;
                  return (
                    <div key={idx}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-slate-700">{tip.tipologia || 'Non specificato'}</span>
                        <span className="text-sm font-bold text-[#0055BB]">{tip.count}</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-[#0055BB] h-2 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">Nessun dato disponibile</p>
            )}
          </CardContent>
        </Card>

        {/* Distribuzione Mensile */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#008A55]" />
              Distribuzione Mensile Presenze
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statsData?.distribuzione_mensile?.length > 0 ? (
              <div className="space-y-2">
                {statsData.distribuzione_mensile.map((m, idx) => {
                  const maxPresenze = Math.max(...statsData.distribuzione_mensile.map(x => x.presenze));
                  const percentage = maxPresenze > 0 ? (m.presenze / maxPresenze) * 100 : 0;
                  const mese = m.mese.split('-')[1];
                  const mesiNomi = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
                  const meseNome = mesiNomi[parseInt(mese, 10) - 1] || mese;
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <span className="w-10 text-sm text-slate-500">{meseNome}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-4">
                        <div 
                          className="bg-[#008A55] h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                          style={{ width: `${Math.max(percentage, 10)}%` }}
                        >
                          <span className="text-xs text-white font-medium">{m.presenze}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">Nessun dato disponibile</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Eventi */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#0055BB]" />
            Eventi con Più Partecipanti
          </CardTitle>
          <CardDescription>Gli eventi che hanno coinvolto più volontari nel {anno}</CardDescription>
        </CardHeader>
        <CardContent>
          {statsData?.distribuzione_volontari_per_attivita?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-4 py-3 text-left">Evento</th>
                    <th className="px-4 py-3 text-left">Data</th>
                    <th className="px-4 py-3 text-right">Volontari</th>
                  </tr>
                </thead>
                <tbody>
                  {statsData.distribuzione_volontari_per_attivita.slice(0, 10).map((evt, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">{evt.titolo}</td>
                      <td className="px-4 py-3 text-slate-600">{evt.data}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#0055BB]/10 text-[#0055BB] font-bold">
                          {evt.num_volontari}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">Nessun dato disponibile</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
